import { useCallback, useEffect, useRef, useState } from 'react';
import { GameFrame, PromptLine, shuffle, useGame, useRoundTimer, type GameProps } from '../kit/shell';
import { Chip, DropSpot, usePlacer } from '../kit/place';
import { KINDS } from '../../concepts/kinds';
import Pic from '../../media/Pic';

type Piece = { key: string; text: string };

const INSTRUCTION = 'Put the words in order to say what is in the picture.';
const MAX_WORDS = 10;

export function wordsOf(sentence: string): string[] {
  return sentence.trim().split(/\s+/).filter(Boolean).slice(0, MAX_WORDS);
}

export function ScenePicture({ picture, className }: { picture: string; className?: string }) {
  if (picture.startsWith('/')) return <img className={className} src={picture} alt="" draggable={false} />;
  return <Pic text={picture} className={className} fallback={<div className={`${className ?? ''} g-scene-missing`}>Picture not found</div>} />;
}

export default function PictureSentence(props: GameProps) {
  const game = useGame(props);
  const picture = typeof game.config?.picture === 'string' ? game.config.picture : '';
  const sentences = ((game.config?.sentences ?? []) as unknown[])
    .filter((s): s is string => typeof s === 'string' && wordsOf(s).length >= 2);
  const speakCards = game.bool('chipSpeech', true);
  const [words, setWords] = useState<string[]>([]);
  const [pieces, setPieces] = useState<Piece[]>([]);
  const [slots, setSlots] = useState<(Piece | null)[]>([]);
  const [trials, setTrials] = useState<string[]>([]);
  const [bounce, setBounce] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const busy = useRef(false);
  const deck = useRef<string[]>([]);
  const last = useRef<string | null>(null);

  const run = useRef(0);

  const peekSentence = useCallback((): string => {
    if (deck.current.length === 0) {
      let fresh = shuffle(sentences);
      if (fresh.length > 1 && fresh[0] === last.current) fresh = [...fresh.slice(1), fresh[0]];
      deck.current = fresh;
    }
    return deck.current[0];
  }, [sentences.join('|')]);

  const start = useCallback(async () => {
    if (sentences.length === 0 || !picture) return;
    const mine = ++run.current;
    const sentence = peekSentence();
    const ws = wordsOf(sentence);
    setDone(false);
    const ids: string[] = [];
    for (let i = 0; i < ws.length; i++) {
      const id = await game.register({ sentence, slot: i + 1, pieces: ws, picture }, { value: ws[i] });
      if (!id) return;
      ids.push(id);
    }
    if (mine !== run.current) return;
    deck.current.shift();
    last.current = sentence;
    setWords(ws);
    setPieces(shuffle(ws.map((text, i) => ({ key: `${i}`, text }))));
    setSlots(ws.map(() => null));
    setTrials(ids);
  }, [peekSentence, picture]);

  useEffect(() => { if (game.config) void start(); }, [game.config, start]);
  useEffect(() => {
    if (words.length === 0 || game.textPrompt) return;
    const t = window.setTimeout(() => game.say(INSTRUCTION), 300);
    return () => window.clearTimeout(t);
  }, [words]);

  const placer = usePlacer((pieceKey, slotId) => { void drop(pieceKey, Number(slotId.slice(5))); });

  async function drop(pieceKey: string, slot: number) {
    if (busy.current || slots[slot]) return;
    const piece = pieces.find((p) => p.key === pieceKey);
    if (!piece) return;
    const right = words[slot];
    if (game.errorless && piece.text !== right) { setBounce(pieceKey); window.setTimeout(() => setBounce(null), 400); return; }
    busy.current = true;
    const v = await game.respond(trials[slot], piece.text);
    if (v?.correct) {
      const next = [...slots];
      next[slot] = piece;
      setSlots(next);
      busy.current = false;
      if (next.every(Boolean)) setDone(true);
      return;
    }
    setBounce(pieceKey);
    window.setTimeout(() => setBounce(null), 400);
    const fresh = await game.register({ sentence: words.join(' '), slot: slot + 1, retry: true, picture }, { value: right });
    busy.current = false;
    if (fresh) setTrials((t) => t.map((x, i) => (i === slot ? fresh : x)));
  }

  const fading = useRoundTimer(done, game.transitionMs, () => { void start(); }, 1200);
  const used = new Set(slots.filter(Boolean).map((p) => p!.key));
  const empty = sentences.length === 0 || !picture;

  return (
    <GameFrame game={game} empty={empty} emptyText={`${KINDS.pictureSentence.describes} Edit it to add a picture and sentences.`} onResume={() => { void start(); }} breakTrial={trials[0] ?? null}>
      {words.length > 0 && (
        <div className={`g-board g-slots g-picsent${fading ? ' is-fading' : ''}`}>
          <ScenePicture picture={picture} className="g-scene-pic" />
          <PromptLine game={game} text={INSTRUCTION} />
          <div className="g-slot-row" role="group" aria-label="Spaces">
            {slots.map((p, i) => (
              p ? (
                <span key={i} className="g-slot is-filled"><span>{p.text}</span></span>
              ) : (
                <DropSpot key={i} placer={placer} id={`slot-${i}`} label={`Space ${i + 1}`} className="g-slot">
                  <span className="g-slot-n">{i + 1}</span>
                </DropSpot>
              )
            ))}
          </div>
          <div className="g-tray" role="group" aria-label="Words">
            {pieces.filter((p) => !used.has(p.key)).map((p) => (
              <Chip key={p.key} placer={placer} id={p.key} label={p.text} className={bounce === p.key ? 'is-bounce' : ''} onTap={() => { if (speakCards) game.say(p.text); }}>
                <span className="g-chip-word">{p.text}</span>
              </Chip>
            ))}
          </div>
        </div>
      )}
    </GameFrame>
  );
}
